import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { CircleCheckSolid } from "~/hec/assets/icons";
import { BodyCopy } from "~/hec/components/ui/body-copy";
import useLearningCenterMutation from "~/hooks/learning-center/use-learning-center-mutation";
import useLearningCenter from "~/hooks/use-learning-center";
import { cn } from "~/lib/ui";
import { safeSessionStorage } from "~/lib/utils/safe-storage";

import messages from "./messages";
import { goalBenefitNames } from "./questionnaire-logic";
import type { GoalData } from "./types";

/**
 * GoalResult — the recommended-goal panel (SFCC-11290). Ported from composable
 * `goalResult.jsx`.
 *
 * Registered: "Start Learning" → `startGoal` (or `openGoal` if a goal is already
 * active) → navigate to the goal's dashboard page.
 * Guest: "Sign in to Learn More" → open the global AccountDrawer via the
 * `#login` hash (the drawer listens for it) and stash resume flags in
 * sessionStorage; on the post-login re-render this same panel detects the flags
 * and completes the start + redirect.
 */
const RESUME_FLAG = "redirectToGoalAfterLogin";
const RESUME_GOAL_ID = "goalIdToStart";
const RESUME_DASHBOARD = "goalDashboardUrl";

interface GoalResultProps {
  goal: GoalData;
  isMobile: boolean;
  isGuest: boolean;
  "data-testid"?: string;
}

const GoalResult = ({
  goal,
  isMobile,
  isGuest,
  "data-testid": dataTestId,
}: GoalResultProps) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { getActiveGoal } = useLearningCenter();
  const { startGoal, openGoal } = useLearningCenterMutation();
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [resume, setResume] = useState(false);
  const activeGoal = getActiveGoal();

  const { id: goalId, title, description, dashboard } = goal;
  const dashboardUrl = dashboard?.value?.data?.url;

  const benefitValues = useMemo(() => goalBenefitNames(goal), [goal]);

  const goalRef = useRef<HTMLDivElement>(null);
  // The redirect target captured for a post-login resume. Stashed in
  // sessionStorage at sign-in time because the live `goal.dashboard` ref may not
  // have re-resolved by the time this panel re-mounts post-login.
  const resumeUrlRef = useRef<string | undefined>(undefined);

  const startAndRedirect = async (targetUrl?: string) => {
    if (!goalId) return;
    setRequestInProgress(true);
    // Start the goal unless the shopper already has an active goal, in which
    // case just open it (composable parity — never displaces the active goal).
    const response = activeGoal
      ? await openGoal(goalId)
      : await startGoal(goalId);
    setRequestInProgress(false);
    if (!response.success) {
      console.error(
        "[GoalQuestionnaire] failed to start goal:",
        response.errorMsg,
      );
      return;
    }
    if (targetUrl) navigate(targetUrl);
  };

  const handleClick = () => void startAndRedirect(dashboardUrl);

  const handleSignInClick = () => {
    // Open the AccountDrawer (it listens for the `#login` hash) and stash the
    // resume target so the post-login re-render completes the start + redirect.
    window.location.hash = "login";
    // SFCC-11876 — storage-safe (blocked storage throws on access).
    if (goalId) safeSessionStorage.setItem(RESUME_GOAL_ID, goalId);
    if (dashboardUrl)
      safeSessionStorage.setItem(RESUME_DASHBOARD, dashboardUrl);
    safeSessionStorage.setItem(RESUME_FLAG, "true");
  };

  // Post-login resume: once the shopper is no longer a guest and the stashed
  // goal matches this panel, fire the start + redirect. Capture the stashed
  // dashboard BEFORE clearing the flags (fallback to the live url) so the
  // shopper is never enrolled-but-stranded. Removing the flags makes this
  // idempotent — only the first-mounted panel resumes.
  useEffect(() => {
    if (isGuest) return;
    if (
      safeSessionStorage.getItem(RESUME_FLAG) === "true" &&
      safeSessionStorage.getItem(RESUME_GOAL_ID) === goalId
    ) {
      resumeUrlRef.current =
        safeSessionStorage.getItem(RESUME_DASHBOARD) ??
        dashboardUrl ??
        undefined;
      safeSessionStorage.removeItem(RESUME_FLAG);
      safeSessionStorage.removeItem(RESUME_GOAL_ID);
      safeSessionStorage.removeItem(RESUME_DASHBOARD);
      setResume(true);
    }
  }, [isGuest, goalId, dashboardUrl]);

  useEffect(() => {
    if (resume) void startAndRedirect(resumeUrlRef.current ?? dashboardUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume]);

  // Mobile: scroll the result into view when it appears (and on goal change).
  useEffect(() => {
    if (isMobile && goalRef.current) {
      goalRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isMobile, goalId]);

  const goalAndDescription = (
    <div>
      <BodyCopy
        className="pb-3 !font-medium text-accent"
        data-testid="goalResult-title"
      >
        {intl.formatMessage(messages.goalLabel, { title })}
      </BodyCopy>
      {description && (
        <BodyCopy data-testid="goalResult-description">{description}</BodyCopy>
      )}
    </div>
  );

  const benefitList = benefitValues.length > 0 && (
    <div className="mt-8">
      <BodyCopy
        size="small"
        className="pb-1 !font-medium text-accent"
        data-testid="goalResult-benefits-heading"
      >
        {intl.formatMessage(messages.benefitsHeading)}
      </BodyCopy>
      {benefitValues.map((benefit, i) => (
        <div
          key={`benefit-${i}`}
          className="flex items-start space-x-2 py-1 text-sm text-black"
        >
          <CircleCheckSolid className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
          <span>{benefit}</span>
        </div>
      ))}
    </div>
  );

  const ctaButton = isGuest ? (
    <button
      type="button"
      onClick={handleSignInClick}
      className="mt-6 bg-accent px-6 py-4 text-center font-medium text-primary-foreground hover:bg-accent"
      data-testid={dataTestId || "goalResult-SignIn"}
    >
      {intl.formatMessage(messages.signInToLearnMore)}
    </button>
  ) : (
    <button
      type="button"
      onClick={handleClick}
      disabled={requestInProgress}
      aria-busy={requestInProgress}
      className="mt-6 inline-flex items-center justify-center gap-2 bg-accent px-6 py-4 text-center font-medium text-primary-foreground hover:bg-accent disabled:cursor-wait disabled:opacity-60"
      data-testid={dataTestId || "goalResult-StartLearning"}
    >
      {/* Loyalty/start CTA loading state (SFCC-11724): the goal start enrolls
          the learner (and, on HSA, the loyalty path), which resolves
          asynchronously — show a spinner + "Starting…" rather than a silent
          disabled button so the CTA reflects the in-flight request. */}
      {requestInProgress && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
          data-testid="goalResult-StartLearning-spinner"
        />
      )}
      {requestInProgress
        ? intl.formatMessage(messages.starting)
        : intl.formatMessage(messages.startLearning)}
    </button>
  );

  return isMobile ? (
    <div ref={goalRef} className="mx-4 mb-8">
      <div className="rounded-md bg-background p-4">
        {goalAndDescription}
        {benefitList}
      </div>
      {ctaButton}
    </div>
  ) : (
    <div
      className={cn("flex flex-col items-start rounded-md bg-background p-10")}
    >
      {goalAndDescription}
      {benefitList}
      {ctaButton}
    </div>
  );
};

export default GoalResult;
